<template>
  <div
    v-if="post"
    class="VideoComp"
  >
    <div
      v-if="post.transcription || sources.length > 1"
      class="mb-4 md:mb-6 flex items-center flex-wrap flex-col md:flex-row"
    >
      <button
        v-if="post.transcription"
        id="video-transcript-accordion-toggle"
        :class="[
          // GTM
          `video-transcript-accordion-${tscriptVisible ? 'open' : 'closed'}`,
          controlLinkClasses
        ]"
        :title="transcriptToggle.title"
        v-html="transcriptToggle.text"
        @click="tscriptVisible = !tscriptVisible"
      />
      <div class="hidden md:block flex-1" />
      <button
        v-if="sources.length > 1 && altIframe"
        id="video-alternate-toggle"
        :class="[
          // GTM
          `video-alternate-${altActive ? 'active' : 'inactive'}`,
          controlLinkClasses
        ]"
        :title="altToggle.title"
        v-html="altToggle.text"
        @click="altActive = !altActive"
      />
      <span
        v-if="sources.length > 1 && mainVideo && adVideo && altIframe && !altActive"
        class="mx-3 hidden md:inline-block"
        v-html="'|'"
      />
      <button
        v-if="sources.length > 1 && mainVideo && adVideo && !altActive"
        id="video-audio-described-toggle"
        :class="[
          // GTM
          `video-audio-described-${adActive ? 'active' : 'inactive'}`,
          controlLinkClasses
        ]"
        :title="adToggle.title"
        v-html="adToggle.text"
        @click="adActive = !adActive"
      />
    </div>
    <Collapsible
      v-if="post.transcription"
      v-show="tscriptVisible"
      class="mb-4 md:mb-6 bg-white text-black"
    >
      <div
        class="tiny-mce px-7 py-5"
        v-html="wysiwygFilter(post.transcription)"
      />
    </Collapsible>
    <div
      class="relative w-full bg-black overflow-hidden"
      :style="{ paddingBottom: '56.25%' }"
    >
      <div class="absolute left-1/2 top-1/2 transform-translate-x--1/2-y--1/2">
        <div
          class="anim-spin w-12 h-12 border-4 rounded-full"
          style="border-color: #fff rgba(255,255,255,0.5) rgba(255,255,255,0.5);"
        />
      </div>
      <transition name="fade">
        <div
          v-if="altActive && altIframe"
          :key="`video-${post.id}-alternative`"
          class="client-iframe-container a-cover overflow-hidden"
          v-html="altIframe"
        />
        <iframe
          v-else-if="iframeSrc"
          :key="`video-${post.id}-${adActive ? 'main' : 'ad'}`"
          :id="`video-${post.id}-${adActive ? 'main' : 'ad'}`"
          class="a-cover"
          :src="iframeSrc"
          type="text/html"
          width="100%"
          height="100%"
          frameborder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
          :title="htmlDecode(iframeTitle)"
          :aria-label="htmlDecode(iframeTitle)"
        />
        <div
          v-else
          key="video-error"
          class="error-container a-cover overflow-hidden bg-black text-white"
        >
          <p
            class="absolute left-0 top-1/2 w-full transform-translate-x-0-y--1/2 px-4 text-center text-xl"
            v-html="'Error loading source'"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import Collapsible from '@/components/utility/Collapsible.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'Video',

  components: {
    Collapsible,
  },

  props: {
    post: {
      type: Object,
      default: () => {}
    }
  },

  data: () => ({
    controlLinkClasses: 'p-1 inline-block underline focus:no-underline lg:hover:no-underline',
    // states
    altActive: false,
    adActive: false,
    tscriptVisible: false
  }),

  computed: {
    ...mapGetters([ 'myStore' ]),
    // Data
    mainVideo() { return this.post && this.post.video },
    adVideo() { return this.post && this.post.video_ad },
    activeVideoId() { return (this.adActive && this.adVideo) || this.mainVideo },
    youtubeId() { return this.getYouTubeId(this.activeVideoId) },
    vimeoId() { return this.getVimeoId(this.activeVideoId) },
    altIframe() {
      const iframeHtml = this.post.international_video_fallback
      if (!iframeHtml) { return }
      // Validte the HTML
      const $el = document.createElement('div')
      $el.innerHTML = iframeHtml
      return $el.innerHTML
    },
    sources() { return [this.mainVideo, this.adVideo, this.altIframe].filter(Boolean) },
    // Video
    embedType() {
      if (this.youtubeId) { return 'youtube' }
      if (this.vimeoId) { return 'vimeo' }
    },
    iframeSrc() {
      const { youtubeId, vimeoId, embedType } = this
      const ytSrc = `https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${window.location.origin}`
      const vimSrc = `https://player.vimeo.com/video/${vimeoId}?portrait=0`
      return (embedType === 'youtube' && ytSrc) || (embedType === 'vimeo' && vimSrc)
    },
    iframeTitle() {
      const { post, embedType } = this
      const title = (embedType === 'youtube' && 'YouTube') || (embedType === 'vimeo' && 'Vimeo')
      const prefix = title ? `${title}: ` : ''
      return `${prefix}${post.title}`
    },
    // Interaction
    storeVideoLabels() { return this.myStore(['labels', 'media_screens', 'video']) ?? {} },
    transcriptToggle() { return this.videoLabels('transcript', this.tscriptVisible) },
    altToggle() { return this.videoLabels('alternate_source', this.altActive) },
    adToggle() { return this.videoLabels('ad_source', this.adActive) },
    forceAlt() { return this.sources.length === 1 && this.altIframe }
  },

  watch: {
    post() { this.reset() }
  },

  beforeMount() {
    if (this.forceAlt) { this.altActive = true }
  },

  methods: {
    videoLabels(key_prefix, activeSetting) {
      if (!key_prefix) { return {} }
      const textObj = activeSetting ? (this.storeVideoLabels[`${key_prefix}_hide`] ?? {}) : (this.storeVideoLabels[`${key_prefix}_show`] ?? {})
      return { text: textObj.btn_text, title: textObj.btn_title }
    },
    reset() {
      this.adActive = false
      this.tscriptVisible = false
    },
    getYouTubeId(inputUrl) {
      const regSplit = inputUrl.split(/(vi\/|v%3D|v=|\/v\/|youtu\.be\/|\/embed\/)/)
      const output = (regSplit[2] !== undefined ? regSplit[2].split(/[^0-9a-z_-]/i)[0] : regSplit[0])
      if (output === inputUrl) { return }
      return output
    },
    getVimeoId(inputUrl) {
      const regMatch = inputUrl.match(/https?:\/\/(?:www\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|)(\d+)(?:$|\/|\?)/)
      const output = regMatch && regMatch[3]
      if (output === inputUrl) { return }
      return output
    }
  }
}
</script>
